import { Edit } from '@mui/icons-material';
import {
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { getPendingAccountsAvailablesForPaymentRequest } from 'lib/api';
import { createPaymentRequestPassive } from 'lib/api/payments';
import FilterComponent from 'lib/common/FilterNonSearch';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { formatMoney, showAlert } from 'lib/functions/utils';
import { PendingAccount } from 'lib/types/models';
import DocumentsSuggestedContainer from 'pages/Finance/Components/Financing/common/documentSugestion';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import EditOperationDateDialog from './EditOperationDateDialog';

interface GeneratePaymentRequestPassiveStepProps {
  stepBack: () => void;
  nextStep: () => void;
  setSelectedRequest: React.Dispatch<React.SetStateAction<number | null>>;
  setFailureOpen: (message: string) => void;
}

function GeneratePaymentRequestPassiveStep({
  stepBack,
  nextStep,
  setSelectedRequest,
  setFailureOpen,
}: GeneratePaymentRequestPassiveStepProps) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [pendingAccounts, setPendingAccounts] = useState<PendingAccount[]>([]);
  const [filteredAccounts, setFilteredAccounts] = useState<PendingAccount[]>(
    [],
  );
  const [selectedPendingAccounts, setSelectedPendingAccounts] = useState<
    number[]
  >([]);
  const [documents, setDocuments] = useState([]);
  const [documentsToApply, setDocumentsToApply] = useState([]);
  const [documentToApplySelected, setDocumentToApplySelected] = useState([]);
  const [maxAmount, setMaxAmount] = useState(0);
  const [amountToApply, setAmountToApply] = useState(0);

  const [openEditOprationDate, setOpenEditOperationDate] = useState(false);
  const [operationDate, setOperationDate] = useState(new Date());

  const getMaxAmount = () => {
    const max = selectedPendingAccounts.reduce((acc, doc) => {
      const document = pendingAccounts.find((x) => x.id === doc);
      if (!document) return acc;
      return acc + Number(document.amountToConciliate);
    }, 0);
    setMaxAmount(max);
  };

  const handleAmountToApply = (amount: string) => {
    setAmountToApply(parseInt(amount));
  };

  const handleChangeAmountToConciliate = (event: any) => {
    const amount = event.target.value.split('.').join('').split('$').join('');
    const document = pendingAccounts.find(
      (inv) => inv.id === event.target.name,
    );
    if (!document) return;
    if (isNaN(amount)) return;

    if (amount > document.amountAvailable) {
      showAlert(
        dispatch,
        'warning',
        'El monto a conciliar no puede ser mayor al monto disponible',
      );
      return;
    }

    document.amountToConciliate = amount;
    setDocuments([...documents]);
    getMaxAmount();
  };

  const pendingAccountsHeaders = [
    { key: 'folio', label: 'Id Cuenta' },
    { key: 'businessName', label: 'Acreedor' },
    { key: 'businessIdentifier', label: 'Rut Acreedor' },
    { key: 'category', label: 'Categoría' },
    { key: 'observation', label: 'Observación' },
    { key: 'issuedDate', label: 'Fecha', type: 'date' },
    { key: 'amount', label: 'Monto original', type: 'money' },
    { key: 'amountAvailable', label: 'Monto disp.', type: 'money' },
    {
      key: 'amountToConciliate',
      label: 'Monto a descontar',
      handleChange: handleChangeAmountToConciliate,
      type: 'money',
    },
  ];

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Filtro',
      action: () => {},
      color: 'primary',
      customButton: (
        <FilterComponent
          data={pendingAccounts}
          filterFields={[
            { key: 'businessName', filterName: 'Empresa Acreedora' },
            { key: 'category', filterName: 'Categoría' },
            { key: 'folioOrigin', filterName: 'Folio Doc. Origen' },
            { key: 'businessNameOrigin', filterName: 'Emisor Origen' },
          ]}
          setFilteredData={setFilteredAccounts}
        />
      ),
    },
  ];

  function getStakeholderData(): {
    businessName?: string;
    businessIdentifier?: string;
  } {
    if (!selectedPendingAccounts.length) return {};

    const document = pendingAccounts.find(
      (invoice) => invoice.id === selectedPendingAccounts[0],
    );

    if (!document) return {};

    return {
      businessName: document.businessName,
      businessIdentifier: document.businessIdentifier,
    };
  }

  const cantSelectRow = (row: any) => {
    const { businessIdentifier } = getStakeholderData();
    if (businessIdentifier && row.businessIdentifier !== businessIdentifier)
      return true;
    return false;
  };

  const totalAmountToConciliate = pendingAccounts.reduce((acc, doc) => {
    if (selectedPendingAccounts.includes(doc.id)) {
      return acc + Number(doc.amountToConciliate);
    }
    return acc;
  }, 0);

  const isNextButtonDisabled = () => {
    if (totalAmountToConciliate - amountToApply < 0) return true;

    if (selectedPendingAccounts.length === 0) return true;

    return false;
  };

  const setUpDocuments = async () => {
    setLoading(true);
    try {
      const response =
        await getPendingAccountsAvailablesForPaymentRequest(dispatch);
      setPendingAccounts(response);
      setFilteredAccounts(response);
    } catch (error) {
      setFailureOpen('Error al cargar los documentos');
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const documentsToPay = pendingAccounts.filter((doc) => {
        return selectedPendingAccounts.includes(doc.id);
      });
      const response = await createPaymentRequestPassive(dispatch, {
        amount: totalAmountToConciliate - amountToApply,
        documentsToApply: documentsToApply.map((doc: any) => {
          return {
            id: doc.id,
            amountToConciliate: doc.amountToConciliate,
          };
        }),
        documentsToPay,
        executedAt: operationDate,
      });
      setSelectedRequest(response.id);
    } catch (error) {
      console.error(error);
      setFailureOpen('Error al crear la devolución de pasivos');
    }
    setLoading(false);
    nextStep();
  };

  const { businessIdentifier } = getStakeholderData();

  useEffect(() => {
    void setUpDocuments();
  }, []);

  useEffect(getMaxAmount, [selectedPendingAccounts]);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <>
      <DialogTitle>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">Crear devolución de pasivos</Typography>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1">
              Fecha de operación: {operationDate?.toISOString().split('T')[0]}
            </Typography>
            <IconButton
              onClick={() => setOpenEditOperationDate(true)}
              style={{ marginLeft: '5px' }}
            >
              <Edit />
            </IconButton>
          </div>
        </div>
      </DialogTitle>
      <DialogContent style={{ padding: '0px 24px' }}>
        <HeaderWithActions
          title="Seleccione las cuentas pendientes a pagar"
          variant="body1"
          type="subHeader"
          buttons={buttonsData}
        />
        <EnhancedTable
          data={filteredAccounts}
          dataHeaders={pendingAccountsHeaders}
          selected={selectedPendingAccounts}
          setSelected={setSelectedPendingAccounts}
          rowsPerPageDefault={5}
          cantSelectRow={cantSelectRow}
          exportToFile="xlsx"
        />
        <div style={{ padding: '16px 0px' }}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Monto a descontar
              </Typography>
              <TextField
                fullWidth
                disabled
                value={formatMoney(totalAmountToConciliate)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiInputBase-input': {
                    textAlign: 'right',
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Aplicar documentos
              </Typography>
              <DocumentsSuggestedContainer
                businessIdentifier={businessIdentifier}
                amountToApply={amountToApply}
                setAmountToApply={handleAmountToApply}
                selected={documentToApplySelected}
                setSelected={setDocumentToApplySelected}
                documents={documents}
                maxAmount={maxAmount}
                setDocuments={setDocuments}
                setDocumentsToApply={setDocumentsToApply}
                documentsToApply={documentsToApply}
                operationDate={operationDate}
                smallTextField
              />
            </Grid>
          </Grid>
        </div>
        <div
          style={{
            padding: '12px 0px',
            marginTop: '8px',
            borderTop: '1px solid #e0e0e0',
          }}
        >
          <Grid container alignItems={'center'} spacing={2}>
            <Grid item xs={8}>
              <Typography variant="body1" margin={'4px'} fontWeight="bold">
                Total a girar:
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <div style={{ textAlign: 'right' }}>
                <Chip
                  label={formatMoney(totalAmountToConciliate - amountToApply)}
                  color={
                    totalAmountToConciliate - amountToApply >= 0
                      ? 'success'
                      : 'error'
                  }
                  variant="outlined"
                  sx={{ fontWeight: 'bold', fontSize: '1.1em' }}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={stepBack}>
          {' '}
          Cancelar{' '}
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={handleSubmit}
          disabled={isNextButtonDisabled()}
        >
          {' '}
          {'Crear'}{' '}
        </CustomButton>
      </DialogActions>
      <EditOperationDateDialog
        open={openEditOprationDate}
        onClose={() => setOpenEditOperationDate(false)}
        operationDate={operationDate}
        setOperationDate={setOperationDate}
      />
    </>
  );
}

export default GeneratePaymentRequestPassiveStep;
